import { EditableValue } from 'components'
import { motion, type Transition } from 'framer-motion'
import { cn } from 'lib/utils'
import { Camera, Loader2, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useLogoUpload } from '../hooks/useLogoUpload'

/**
 * The brand-profile field language: identity logo, quiet labeled rows,
 * always-live editable text, chip groups, and round color dots. Used by the
 * My Brand settings page.
 *
 * The framer-motion layout props (`layoutId` / `layoutTransition` /
 * `layoutDependency`) exist for the onboarding ScanningStep→panel morph
 * only — the settings page never passes them.
 */

/**
 * Labelled brand value, editable in place. A thin adapter over
 * `<EditableValue>` — it keeps this module's `onSave`-only contract and its
 * `dimmed` / `underline` / `displayLayoutId` vocabulary so the My Brand page
 * doesn't have to know about the primitive.
 */
export function EditableText({
  value,
  placeholder,
  onSave,
  className = 'text-[15px]',
  dimmed = false,
  underline = true,
  maxLines,
  allowEmpty = false,
  displayLayoutId,
  displayLayoutTransition,
  displayLayoutDependency,
}: {
  value: string
  placeholder: string
  onSave: (next: string) => void
  /** Typography classes for the field. */
  className?: string
  /** Render the value in the faint tier (identity-card subline). */
  dimmed?: boolean
  /** Dashed editable underline; the identity card renders bare text. */
  underline?: boolean
  /** Cap the field at this many lines; longer content scrolls inside it. */
  maxLines?: number
  /** Commit an empty value as a save (clearable field) instead of ignoring it. */
  allowEmpty?: boolean
  /**
   * Shared layoutId on the displayed value so it can receive the onboarding
   * scanning morph.
   *
   * Pass it ONLY while the morph is actually in flight and drop it the moment
   * it settles. It renders a stand-in span over the field and makes the
   * field's own text transparent, so a layoutId left set permanently would
   * leave the user typing into invisible text. (No current caller passes it —
   * both My Brand call sites omit it.)
   */
  displayLayoutId?: string
  displayLayoutTransition?: Transition
  displayLayoutDependency?: unknown
}) {
  return (
    <EditableValue
      value={value}
      placeholder={placeholder}
      label={placeholder}
      // Every field here is a short identifier, never prose.
      singleLine
      underline={underline}
      maxLines={maxLines}
      pencilSize={dimmed ? 10 : 12}
      // A field that isn't clearable treats "emptied" as a slip, not an edit,
      // and snaps back to the value it had. Dropping the save alone isn't
      // enough here — nothing would repaint the field, so it would sit there
      // looking blank while the store still held the value.
      revertWhenEmpty={!allowEmpty}
      onSave={onSave}
      overlay={
        displayLayoutId ? (
          <motion.span
            layoutId={displayLayoutId}
            layoutDependency={displayLayoutDependency}
            transition={displayLayoutTransition}
            className="inline-block !opacity-100"
          >
            {value || placeholder}
          </motion.span>
        ) : undefined
      }
      className={cn(className, dimmed ? 'text-paper-faint' : 'text-paper')}
    />
  )
}

/**
 * Click-to-upload brand logo. Shows the logo (or the business initial) in a
 * 72px round chip; clicking opens a file picker, hover reveals a camera badge,
 * and an X removes it. Writes the attachment id to `brandKit.logoId` (optimistic
 * blob → publicUrl locally, id persisted to the server).
 */
export function LogoEditor({
  logo,
  name,
  layoutId,
  layoutTransition,
  layoutDependency,
  onUploaded,
  onRemoved,
}: {
  logo?: string | null
  name: string
  /** Shared layoutId while the onboarding scanning morph is in flight. */
  layoutId?: string
  layoutTransition?: Transition
  layoutDependency?: unknown
  /** Fired after a successful upload / removal (analytics hooks). */
  onUploaded?: () => void
  onRemoved?: () => void
}) {
  const { uploading, uploadLogo, removeLogo } = useLogoUpload()
  const [broken, setBroken] = useState(false)

  const onUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      setBroken(false)
      await uploadLogo(file)
      onUploaded?.()
    },
    [uploadLogo, onUploaded]
  )

  const onRemove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      void removeLogo()
      onRemoved?.()
    },
    [removeLogo, onRemoved]
  )

  const isUrl =
    !!logo &&
    (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('blob:'))
  const showImage = isUrl && !broken

  return (
    <div className="group/logo relative h-[72px] w-[72px] flex-shrink-0">
      <label
        className="flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-full border border-line-bright bg-white"
        title="Tap to change logo"
      >
        {showImage ? (
          <motion.img
            // Remount when the morph settles: if the flight was interrupted
            // mid-air, dropping the layoutId on the SAME instance can leave
            // it parked at a stale mid-flight transform. A fresh instance
            // renders at its natural slot.
            key={layoutId ? 'morph' : 'settled'}
            layoutId={layoutId}
            layoutDependency={layoutDependency}
            transition={layoutTransition}
            src={logo!}
            alt="Brand logo"
            className={`h-full w-full object-contain ${uploading ? 'opacity-40' : layoutId ? '!opacity-100' : ''}`}
            onError={() => setBroken(true)}
          />
        ) : (
          <span className="text-ink-on-acid text-[30px] font-extrabold">
            {name.trim().charAt(0).toUpperCase() || '·'}
          </span>
        )}
        <input type="file" accept="image/*" className="sr-only" onChange={onUpload} />
        {/* Hover hint that the logo is replaceable. */}
        {!uploading && (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-[rgba(0,0,0,0.45)] opacity-0 transition-opacity group-hover/logo:opacity-100">
            <Camera size={16} className="text-white" />
          </span>
        )}
      </label>
      {uploading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-acid" />
        </span>
      )}
      {showImage && !uploading && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove logo"
          className="absolute -top-1 -right-1 z-10 hidden h-5 w-5 items-center justify-center rounded-full border border-line-bright bg-bg-4 text-paper-dim transition-colors group-hover/logo:flex hover:text-paper"
        >
          <X size={11} />
        </button>
      )}
    </div>
  )
}

/**
 * One editable brand color: a round swatch with a transparent native color
 * input layered on top, so tapping anywhere on (or just around) the dot opens
 * the OS color picker while the dot keeps the exact T3 look.
 */
export function ColorDot({
  hex,
  onChange,
  layoutId,
  layoutTransition,
  layoutDependency,
}: {
  hex: string
  onChange: (next: string) => void
  /** Shared layoutId while the onboarding scanning morph is in flight. */
  layoutId?: string
  layoutTransition?: Transition
  layoutDependency?: unknown
}) {
  return (
    <label className="relative inline-flex h-5 w-5 cursor-pointer" title="Tap to change color">
      <motion.span
        // Remount when the morph settles — see LogoEditor: an interrupted
        // flight can otherwise park the dot at a stale mid-flight transform.
        key={layoutId ? 'morph' : 'settled'}
        layoutId={layoutId}
        layoutDependency={layoutDependency}
        transition={layoutTransition}
        className={`h-full w-full rounded-full border border-line-bright ${layoutId ? '!opacity-100' : ''}`}
        style={{ background: hex }}
      />
      <input
        type="color"
        value={hex}
        onChange={e => onChange(e.target.value)}
        aria-label="Brand color"
        className="absolute -inset-1.5 h-[calc(100%+12px)] w-[calc(100%+12px)] cursor-pointer opacity-0"
      />
    </label>
  )
}
